

// SidebarContainer.jsx
import React, { useState, useRef, useEffect } from 'react';
// import { LayoutGrid } from 'lucide-react'; // No longer needed here for catalog button
import BotSection from './BotSection';
import SidebarHistory from './SidebarHistory';
// BotCatalog is no longer rendered directly by SidebarContainer
import { botManager } from '@/managers/BotManager';
import { useTheme } from '@/managers/ThemeProvider';

const SidebarContainer = ({ isOpen, onClose }) => {
    const { isLightTheme } = useTheme();
    const containerRef = useRef(null);
    const dividerRef = useRef(null);
    const [isDragging, setIsDragging] = useState(false);
    const [botSectionHeight, setBotSectionHeight] = useState('60%');
    // isCatalogOpen state removed, BotSection handles its catalog instance
    
    const [botState, setBotState] = useState(botManager.getState());
    
    useEffect(() => {
        return botManager.subscribe(setBotState);
    }, []);

    useEffect(() => {
        if (!botState.isInitialized && !botState.isLoading) {
            botManager.initialize();
        }
    }, [botState.isInitialized, botState.isLoading]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (
                containerRef.current && 
                !containerRef.current.contains(e.target) && 
                isOpen
            ) {
                onClose();
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen, onClose]);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const handleMouseMove = (e) => {
            if (!isDragging) return;
            const containerRect = container.getBoundingClientRect();
            const mouseY = e.clientY - containerRect.top;
            const containerHeight = containerRect.height;
            const percentage = Math.min(Math.max((mouseY / containerHeight) * 100, 20), 80);
            setBotSectionHeight(`${percentage}%`);
        };

        const handleMouseUp = () => {
            setIsDragging(false);
        };

        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
        }

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDragging]);

    return (
        <>
            {isOpen && (
                <div 
                    className="fixed inset-0 z-[100] bg-transparent" 
                    onClick={onClose} // This overlay click will close the sidebar
                />
            )}

            <div 
                ref={containerRef}
                className={`
                    fixed left-0 top-0 h-screen border-r transform transition-transform duration-300 ease-in-out z-[200]
                    w-[300px] md:w-[320px] flex flex-col
                    ${isOpen ? 'translate-x-0' : '-translate-x-full'}
                    ${isLightTheme ? 'bg-surface border-border-color' : 'bg-surface border-border-color'}
                `}
            >
                <BotSection 
                    onClose={onClose} 
                    containerHeight={botSectionHeight}
                    // onManageBots prop is removed as BotSection handles its catalog opening
                />
                
                <div
                    ref={dividerRef}
                    className={`
                        h-1 cursor-ns-resize flex items-center justify-center transition-colors duration-200
                        ${isLightTheme ? 'bg-border-color hover:bg-primary/20' : 'bg-border-color hover:bg-primary/20'}
                        ${isDragging ? 'bg-primary/20' : ''}
                    `}
                    onMouseDown={(e) => {
                        e.preventDefault();
                        setIsDragging(true);
                    }}
                >
                    <div className={`w-16 h-0.5 rounded-full ${isLightTheme ? 'bg-border-color' : 'bg-border-color'}`} />
                </div>

                <SidebarHistory 
                    containerHeight={`calc(100% - ${botSectionHeight} - 4px)`}
                    onClose={onClose}
                />
            </div>

            {/* BotCatalog is now rendered within BotSection when its state dictates */}
        </>
    );
};

export default SidebarContainer;